import { ROUTES } from '../../routing';
import React from 'react';
import LearningPlan from '../LearningPlan/LearningPlan';
import { H3 } from '../elements/Text';
import StreakCard from './StreakCard';
import styled from 'styled-components';
import {
  AppliedProjectStat,
  LearningPlan as LearningPlanType,
} from '../../hydra';
import { Redirect, useHistory } from 'react-router-dom';
import {
  LEARNING_PLAN_UNIT_TYPE,
  LearningPlanUnit,
} from '../LearningPlan/LearningPlanUnit';
import CTACard from './CTACard';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTeamsProfile } from '../../hooks/use-hydra';
import { useTeamsProjectsDashboard } from '../../hooks/use-hydra/teams';
import { ActiveTrackCard } from './TrackCard';
import { AppliedProjects } from '../../pages/ProjectsDashboard/TeamsProjectsDashboard';
import { TeamsContentModule } from '../../api/teams';
import AssessmentTests from './AssessmentTests';
import CohortMilestones from './CohortMilestones';
import AnnouncementBanner from './AnnouncementBanner';
import useURLQuery from '../../hooks/use-url-query';
import { QUERYPARAM_SHOULD_OPEN_TEST } from '../../pages/Login/loginConstants';

dayjs.extend(utc);

const GridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 32px;
  margin-top: 36px;

  @media screen and (max-width: 664px) {
    display: flex;
    flex-direction: column;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 32px;
  margin: 32px 0px;
`;

export default function TeamsHome() {
  const { value: projects, loading: isLoadingProjects } =
    useTeamsProjectsDashboard();
  const { value: teamsProfile, loading: isLoadingTeams } = useTeamsProfile();
  const urlQuery = useURLQuery();
  const history = useHistory();
  const learningPlan = teamsProfile?.learningPlan ?? null;
  const shouldShowLearningPlan =
    learningPlan && !isLoadingProjects && !isLoadingTeams;
  const teamSlug = teamsProfile?.company?.slug ?? '';

  const latestUnfinishedAssessmentTest = teamsProfile?.assessmentTests
    .filter((test) => !test.finishedAt)
    .sort(
      (a, b) =>
        dayjs.utc(b.startedAt).valueOf() - dayjs.utc(a.startedAt).valueOf()
    )[0];

  const shouldRedirectToTest =
    !isLoadingTeams &&
    urlQuery.get(QUERYPARAM_SHOULD_OPEN_TEST) === 'true' &&
    latestUnfinishedAssessmentTest;

  const latestCohort = teamsProfile?.onboardedCohorts.sort(
    (a, b) =>
      dayjs.utc(b.cohort.startDate).valueOf() -
      dayjs.utc(a.cohort.startDate).valueOf()
  )[0];

  const modules = teamsProfile?.modules ?? [];
  const appliedProjects = (projects ?? [])
    .filter((p) => (learningPlan ?? []).some((l) => l.slug === p.slug))
    .sort(
      (a, b) =>
        (learningPlan?.findIndex((l) => l.slug === a.slug) ?? 0) -
        (learningPlan?.findIndex((l) => l.slug === b.slug) ?? 0)
    );
  const assessmentTests = teamsProfile?.assessmentTests ?? [];
  const hasUnfinishedAssessmentTests = assessmentTests.some(
    (test) => !test.finishedAt
  );

  React.useEffect(() => {
    // NOTE - the .learnerId check is important because testers also have "hasProfile" set to true, while they shouldn't onboard
    if (
      teamsProfile?.hasProfile &&
      teamsProfile?.learnerId &&
      !teamsProfile?.onboardingAnswers
    ) {
      return history.push(
        ROUTES.TEAMS.PROFILE_ONBOARDING.getPath({ teamSlug })
      );
    }
    if (
      teamsProfile?.hasProfile &&
      (teamsProfile?.openCohortInvites ?? []).length > 0
    ) {
      return history.push(
        ROUTES.TEAMS.COHORT_ONBOARDING.getPath({
          teamSlug,
        })
      );
    }
  }, [teamsProfile, history, teamSlug]);

  const teamsLearningPlan = useGetLearningPlan({
    learningPlan: teamsProfile?.learningPlan ?? null,
    projects: appliedProjects,
    contentModules: modules,
    teamSlug,
  });

  const latestCohortKickOffTime =
    latestCohort?.onboardingAnswers?.['ask-kickoff-time'] ?? '';
  const firstKickOffInTheFuture = latestCohortKickOffTime
    ? dayjs.utc(latestCohortKickOffTime).isAfter(dayjs.utc())
    : null;
  const isCompanyKickOffEnabled =
    teamsProfile?.company?.settings?.['KICK_OFF'] === true;

  if (shouldRedirectToTest) {
    return (
      <Redirect
        to={ROUTES.TEAMS.ASSESSMENT_TEST.getPath({
          teamSlug: latestUnfinishedAssessmentTest.companySlug,
          testSlug: latestUnfinishedAssessmentTest.testSlug,
        })}
      />
    );
  }
  return (
    <React.Fragment>
      {firstKickOffInTheFuture && isCompanyKickOffEnabled && (
        <AnnouncementBanner kickOffTime={latestCohortKickOffTime} />
      )}
      <Progress plan={teamsLearningPlan} />
      {assessmentTests.length > 0 && hasUnfinishedAssessmentTests && (
        <AssessmentTests assessmentTests={assessmentTests} />
      )}
      {shouldShowLearningPlan && (
        <LearningPlan
          infoText={`✨ This learning plan has been designed especially for you and your company.`}
          learningPlan={teamsLearningPlan}
        />
      )}
      {latestCohort && <CohortMilestones onboardedCohort={latestCohort} />}
      {assessmentTests.length > 0 && !hasUnfinishedAssessmentTests && (
        <AssessmentTests assessmentTests={assessmentTests} />
      )}
      {appliedProjects.length > 0 && (
        <>
          <H3>Projects</H3>
          <AppliedProjects
            appliedProjects={appliedProjects}
            onGoToProject={(slug: string) =>
              history.push(
                ROUTES.TEAMS.PROJECT.getPath({ teamSlug, projectSlug: slug })
              )
            }
          />
        </>
      )}
      {modules.length > 0 && (
        <>
          <H3>Modules</H3>
          <Modules modules={modules} />
        </>
      )}
    </React.Fragment>
  );
}

function Progress({ plan }: { plan: LearningPlanUnit[] }) {
  const history = useHistory();

  const nextPlanItem =
    plan.find((item) => !item.isCompleted) ?? plan[plan.length - 1];

  if (!nextPlanItem && !plan.length) {
    return null;
  }

  if (!nextPlanItem) {
    return (
      <GridRow style={{ display: 'block' }}>
        <StreakCard />
      </GridRow>
    );
  }

  return (
    <GridRow>
      <CTACard
        title={nextPlanItem.moduleTitle}
        nextItemTitle={nextPlanItem.nextItemTitle}
        description={nextPlanItem.description}
        imageUrl={nextPlanItem.imageUrl}
        dashboardUrl={nextPlanItem.dashboardUrl}
        progressText={nextPlanItem.progressDescription}
        primaryButton={{
          ctaLabel: !nextPlanItem.isCompleted
            ? nextPlanItem.isInProgress
              ? 'Continue'
              : 'Start'
            : 'Dashboard',
          onCTAClick: nextPlanItem.onClick,
        }}
        secondaryButton={
          nextPlanItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE &&
          !nextPlanItem.isCompleted
            ? {
                ctaLabel: 'Dashboard',
                onCTAClick: () => {
                  history.push(nextPlanItem.dashboardUrl ?? '');
                },
              }
            : null
        }
      />
      <StreakCard />
    </GridRow>
  );
}

function Modules({ modules }: { modules: Array<TeamsContentModule> }) {
  return (
    <CardsContainer>
      {modules.map((m) => {
        return (
          <ActiveTrackCard key={`track-card-${m.content.slug}`} module={m} />
        );
      })}
    </CardsContainer>
  );
}

function useGetLearningPlan({
  learningPlan,
  projects,
  contentModules,
  teamSlug,
}: {
  learningPlan: LearningPlanType | null;
  projects: Array<AppliedProjectStat>;
  contentModules: Array<TeamsContentModule>;
  teamSlug: string;
}): Array<LearningPlanUnit> {
  const history = useHistory();
  const learningPlanUnits: Array<LearningPlanUnit> = (learningPlan ?? [])
    .map((unit) => {
      if (unit.type === 'project') {
        const project = projects.find((p) => p.slug === unit.slug);

        if (!project) {
          return null;
        }

        const isCompleted = project.is_finished;
        const isStarted = (project?.completedQuestions ?? -1) > 0;
        const isInProgress = isStarted && !isCompleted;
        const progressDescription =
          isCompleted || !isStarted
            ? `${project.question_count} exercises`
            : `${project?.completedQuestions ?? 0}/${
                project.question_count
              } exercises`;

        return {
          type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
          unitTitle: project?.name ?? '',
          nextItemTitle: project?.name ?? '',
          moduleTitle: project?.topic ?? '',
          subtitle: 'PROJECT',
          slug: unit.slug,
          imageUrl: project?.image_url ?? '',
          description: project?.description ?? '',
          reasoning: unit.reasoning,
          progressDescription,
          isCompleted,
          isInProgress,
          onClick: async () => {
            history.push(
              ROUTES.TEAMS.PROJECT.getPath({
                teamSlug,
                projectSlug: project.slug,
              })
            );
          },
          dashboardUrl: null,
          isLocked: false,
        };
      }

      if (unit.type === 'module') {
        const module = contentModules.find((m) => m.content.slug === unit.slug);
        if (!module) {
          return null;
        }

        const isCompleted =
          // TODO: this OR exists to support the old data structure
          // We can remove this once we've migrated all users to the new data structure
          // of using the finishedCoreLessonsCount field
          module?.finishedCoreLessonsCount === module?.coreLessonsCount ||
          module?.coreAttemptedLessonsCount === module?.coreLessonsCount;
        const isInProgress =
          module?.coreAttemptedLessonsCount > 0 &&
          module?.coreAttemptedLessonsCount < module?.coreLessonsCount;

        const progressDescription =
          module?.coreAttemptedLessonsCount === 0 ||
          module?.coreAttemptedLessonsCount === module?.coreLessonsCount
            ? `${module?.coreLessonsCount} lessons`
            : `${module?.coreAttemptedLessonsCount}/${module?.coreLessonsCount} lessons`;

        return {
          type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
          unitTitle: module.content.title,
          nextItemTitle:
            module.firstIncompleteActivity?.activityName ||
            module.content.title,
          moduleTitle: module.content.title,
          subtitle: 'MODULE',
          description: module.content.description ?? '',
          slug: unit.slug,
          imageUrl: module.content.imageUrl ?? '',
          reasoning: unit.reasoning,
          progressDescription,
          isCompleted,
          isInProgress,
          onClick: async () => {
            if (
              module.firstUnattemptedActivity?.missionSlug &&
              module.firstUnattemptedActivity?.activitySlug
            ) {
              history.push(
                ROUTES.TEAMS.ACTIVITY.getPath({
                  teamSlug,
                  trackSlug: module.trackSlug ?? '',
                  activitySlug:
                    module.firstUnattemptedActivity?.activitySlug ?? '',
                  missionSlug:
                    module.firstUnattemptedActivity?.missionSlug ?? '',
                })
              );
            } else {
              // When they have already completed the entire module
              history.push(
                ROUTES.TEAMS.MODULE_DASHBOARD.getPath({
                  teamSlug,
                  moduleSlug: unit.slug,
                  trackSlug: module.trackSlug ?? '',
                })
              );
            }
          },
          dashboardUrl: ROUTES.TEAMS.MODULE_DASHBOARD.getPath({
            teamSlug,
            moduleSlug: unit.slug,
            trackSlug: module.trackSlug ?? '',
          }),
          isLocked: false,
        };
      }

      throw new Error(
        `Invalid learning unit type: ${(unit as { type: string }).type}`
      );
    })
    .filter((unit): unit is NonNullable<typeof unit> => unit !== null);

  return learningPlanUnits;
}
